import React, { useEffect, useMemo, useRef } from 'react'
import debounce from 'lodash/debounce'
import SearchIcon from '@material-ui/icons/Search'
import { ScheduleFilterController } from '../../../hooks/useFilter'

interface Props {
    filterController: ScheduleFilterController
    onSearchingChange?: (isSearching: boolean) => void
}

/**
 * Native input for maximum typing responsiveness - zero React state on keystroke.
 * The DOM holds the value; no React re-renders, no parent callbacks.
 */
const DEBOUNCE_MS = 400

export const UnitSearchInput = React.memo(
    (props: Props) => {
        const { filterController } = props
        const inputRef = useRef<HTMLInputElement>(null)
        const propsRef = useRef(props)
        propsRef.current = props
        const debouncedUpdateFilter = useMemo(
            () =>
                debounce((value: string) => {
                    propsRef.current.onSearchingChange?.(true)
                    // Double rAF: first lets React render overlay, second runs after paint so overlay is visible before filtering
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            propsRef.current.filterController.setInfrastructureFilter(
                                'unitSearch',
                                value,
                            )
                            setTimeout(() => {
                                propsRef.current.onSearchingChange?.(false)
                            }, 0)
                        })
                    })
                }, DEBOUNCE_MS),
            [],
        )

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            debouncedUpdateFilter(e.target.value)
        }

        // Sync input when filter changes externally (e.g. reset filters)
        useEffect(() => {
            const filterValue = filterController.filter.unitSearch
            if (inputRef.current && inputRef.current.value !== filterValue) {
                inputRef.current.value = filterValue
            }
        }, [filterController.filter.unitSearch])

        useEffect(() => {
            return () => debouncedUpdateFilter.cancel()
        }, [debouncedUpdateFilter])

        return (
            <div style={{ position: 'relative', display: 'inline-flex' }}>
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Unit Search"
                    defaultValue={filterController.filter.unitSearch}
                    onChange={handleChange}
                    style={{
                        padding: '8px 36px 8px 14px',
                        fontSize: 14,
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: 4,
                        outline: 'none',
                        minWidth: 140,
                        marginRight: 8,
                        marginBottom: 8,
                        backgroundColor: '#fff',
                    }}
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                        e.target.style.borderColor = 'rgba(25, 118, 210, 0.5)'
                        e.target.style.boxShadow =
                            '0 0 0 2px rgba(25, 118, 210, 0.25)'
                    }}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)'
                        e.target.style.boxShadow = 'none'
                    }}
                />
                <SearchIcon
                    style={{
                        position: 'absolute',
                        right: 12,
                        top: '40%',
                        transform: 'translateY(-50%)',
                        color: 'rgba(0, 0, 0, 0.54)',
                        pointerEvents: 'none',
                        fontSize: 20,
                    }}
                />
            </div>
        )
    },
    (prev, next) => {
        // Only re-render when filter value changes (e.g. external reset) - prevents lag from parent re-renders
        return (
            prev.filterController.filter.unitSearch ===
                next.filterController.filter.unitSearch &&
            prev.onSearchingChange === next.onSearchingChange
        )
    },
)
